import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarTimes, faPlus, faCheck, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import InternalShell, { useInternalApi } from '../layouts/InternalShell';
import { COMPLIANCE_NAV } from './nav';

interface Schedule {
    id: number; data_category: string; description: string; retention_period_months: number;
    legal_basis: string; disposal_method: string; owner: string; last_reviewed: string | null;
}

const RetentionSchedules: React.FC = () => {
    const { api } = useInternalApi();
    const [schedules, setSchedules] = useState<Schedule[]>([]);
    const [note, setNote] = useState('');
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    const [form, setForm] = useState({ data_category: '', retention_period_months: 120, legal_basis: '', disposal_method: '', owner: '', description: '' });

    useEffect(() => { document.title = 'Retention Schedules - Uzima Internal'; }, []);

    const load = async () => {
        setLoading(true);
        try {
            const res = await api.get('compliance-api/retention-schedules/');
            if (res.data?.status === 'success') {
                setSchedules(res.data.data.schedules || []);
                setNote(res.data.data.note || '');
            }
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const submitCreate = async () => {
        if (!form.data_category.trim() || !form.retention_period_months) {
            Swal.fire({ icon: 'warning', title: 'Missing Fields', text: 'Data category and retention period are required.' });
            return;
        }
        try {
            await api.post('compliance-api/retention-schedules/', form);
            setShowCreate(false);
            setForm({ data_category: '', retention_period_months: 120, legal_basis: '', disposal_method: '', owner: '', description: '' });
            load();
        } catch (err: any) {
            Swal.fire({ icon: 'error', title: 'Error', text: err?.response?.data?.message || 'Could not create schedule.' });
        }
    };

    const markReviewed = async (s: Schedule) => {
        try {
            await api.patch(`compliance-api/retention-schedules/${s.id}/`, {});
            load();
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Could not mark as reviewed.' });
        }
    };

    const isStale = (s: Schedule) => {
        if (!s.last_reviewed) return true;
        const oneYearAgo = new Date();
        oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
        return new Date(s.last_reviewed) < oneYearAgo;
    };

    return (
        <InternalShell activeDashboard="compliance_center" subNav={COMPLIANCE_NAV}>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="fw-bold mb-1"><FontAwesomeIcon icon={faCalendarTimes} className="me-2 text-primary" />Retention Schedules</h2>
                    <p className="text-muted mb-0">How long each data category is kept, and on what legal basis</p>
                </div>
                <button className="btn btn-primary" onClick={() => setShowCreate(true)}><FontAwesomeIcon icon={faPlus} className="me-2" />New Schedule</button>
            </div>

            {note && <div className="alert alert-secondary small"><FontAwesomeIcon icon={faInfoCircle} className="me-2" />{note}</div>}

            <div className="card border-0 shadow-sm">
                <div className="card-body p-0">
                    {loading ? (
                        <div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
                    ) : schedules.length === 0 ? (
                        <div className="text-center py-5 text-muted">No retention schedules defined — the compliance posture rule for this will be failing.</div>
                    ) : (
                        <table className="table table-hover align-middle mb-0">
                            <thead className="table-light"><tr><th>Data Category</th><th>Retention</th><th>Legal Basis</th><th>Disposal</th><th>Owner</th><th>Last Reviewed</th><th></th></tr></thead>
                            <tbody>
                                {schedules.map(s => (
                                    <tr key={s.id} className={isStale(s) ? 'table-warning' : ''}>
                                        <td><strong>{s.data_category}</strong>{s.description && <div className="small text-muted">{s.description}</div>}</td>
                                        <td>{s.retention_period_months} months</td>
                                        <td className="small">{s.legal_basis || '—'}</td>
                                        <td className="small">{s.disposal_method || '—'}</td>
                                        <td className="small">{s.owner || '—'}</td>
                                        <td className="small">
                                            {s.last_reviewed ? new Date(s.last_reviewed).toLocaleDateString() : <span className="text-danger">Never</span>}
                                            {isStale(s) && <div><span className="badge bg-warning">Review due</span></div>}
                                        </td>
                                        <td className="text-end">
                                            <button className="btn btn-sm btn-outline-success" onClick={() => markReviewed(s)}>
                                                <FontAwesomeIcon icon={faCheck} className="me-1" />Reviewed
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {showCreate && (
                <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">New Retention Schedule</h5>
                                <button type="button" className="btn-close" onClick={() => setShowCreate(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Data category</label>
                                    <input className="form-control" placeholder="e.g. Clinical records" value={form.data_category}
                                        onChange={(e) => setForm(f => ({ ...f, data_category: e.target.value }))} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Retention period (months)</label>
                                    <input type="number" className="form-control" value={form.retention_period_months}
                                        onChange={(e) => setForm(f => ({ ...f, retention_period_months: Number(e.target.value) }))} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Legal basis</label>
                                    <input className="form-control" placeholder="e.g. Kenya Health Act 2017" value={form.legal_basis}
                                        onChange={(e) => setForm(f => ({ ...f, legal_basis: e.target.value }))} />
                                </div>
                                <div className="row g-3">
                                    <div className="col-6">
                                        <label className="form-label">Disposal method</label>
                                        <input className="form-control" value={form.disposal_method} onChange={(e) => setForm(f => ({ ...f, disposal_method: e.target.value }))} />
                                    </div>
                                    <div className="col-6">
                                        <label className="form-label">Owner</label>
                                        <input className="form-control" value={form.owner} onChange={(e) => setForm(f => ({ ...f, owner: e.target.value }))} />
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-light" onClick={() => setShowCreate(false)}>Cancel</button>
                                <button className="btn btn-primary" onClick={submitCreate}>Create</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </InternalShell>
    );
};

export default RetentionSchedules;
